import React, { useState, useEffect } from 'react';
import { adviceService } from '@/lib/services';
import { useScopedI18n } from '@/locales/client';
import { toast } from 'sonner';
import {
  TrackingService,
  TrackingEventType,
} from '@/lib/services/tracking.service';
import { useSession } from 'next-auth/react';
import { Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/common/overlay/dialog/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/common/inputs/text/form';
import { Button } from '@/components/common/inputs/button/button';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const FeedbackSchema = z.object({
  comments: z.string().min(1, 'Please provide your feedback'),
});

type FeedbackFormData = z.infer<typeof FeedbackSchema>;

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adviceId: string;
  initialReaction: 'positive' | 'negative';
  existingFeedback?: {
    reaction: 'positive' | 'negative';
    comments: string;
    createdAt: string;
    updatedAt: string;
  } | null;
  question?: string;
  response?: string;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onOpenChange,
  adviceId,
  initialReaction,
  existingFeedback = null,
  question = '',
  response = '',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const t = useScopedI18n('responseFeedback');
  const { data: session } = useSession();

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(FeedbackSchema),
    defaultValues: {
      comments: existingFeedback?.comments || '',
    },
  });

  // Initialize form from existing feedback if available
  useEffect(() => {
    if (existingFeedback) {
      form.reset({
        comments: existingFeedback.comments,
      });
    }
  }, [existingFeedback, form]);

  const onSubmit = async (data: FeedbackFormData) => {
    if (!session) {
      toast.error('You must be logged in to submit feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit feedback using the advice service
      await adviceService.submitFeedback(
        {
          email: session.user?.email || '',
          reaction: initialReaction, // Use the reaction from quick feedback
          flaggedQuestion: question,
          flaggedResponse: response,
          comments: data.comments,
        },
        session
      );

      // Also track the feedback using our tracking system
      TrackingService.trackActivity(TrackingEventType.FEEDBACK, {
        userId: session.user?.id,
        targetId: adviceId,
        session,
        metadata: {
          feedback: data.comments,
          type: existingFeedback ? 'edit' : 'detailed',
          previousReaction: existingFeedback?.reaction,
        },
      });

      toast.success(
        existingFeedback ? 'Feedback updated successfully' : t('success')
      );
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(t('error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='dark:bg-gray-800 sm:max-w-[425px]'>
        <DialogTitle className='text-lg font-semibold text-gray-900 dark:text-white'>
          {t('giveFeedbackTitle')}
        </DialogTitle>
        <DialogDescription className='text-sm text-gray-500 dark:text-gray-400'>
          {existingFeedback
            ? t('giveFeedbackDescription')
            : t('editFeedbackDescription')}
        </DialogDescription>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
            <FormField
              control={form.control}
              name='comments'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <textarea
                      {...field}
                      className='h-32 w-full resize-none rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 dark:focus:ring-purple-400'
                      placeholder={t('placeholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-end space-x-3'>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className='dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              >
                {t('cancel')}
              </Button>

              <Button
                type='submit'
                disabled={isSubmitting}
                className='bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600'
              >
                <Send className='mr-2 h-4 w-4' />
                <span>
                  {isSubmitting
                    ? '...'
                    : existingFeedback
                      ? t('update')
                      : t('submit')}
                </span>
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
